import {
  closestCenter,
  defaultDropAnimationSideEffects,
  DndContext,
  DragEndEvent,
  DropAnimation,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { Button } from 'components/atoms';
import EmptyPlaceholder from 'components/atoms/empty/EmptyPlaceholder';
import ArtistRoleCard from 'components/molecules/card/artist-role/ArtistRoleCard';
import ViewHint from 'components/molecules/hint/ViewHint';
import ArtistRoleSelectPopupForm from 'components/molecules/popup/artist-role-select/ArtistRoleSelectPopupForm';
import ArtistSelectPopupForm from 'components/molecules/popup/artist-select/ArtistSelectPopupForm';
import StepsTab from 'components/molecules/steps/tab/StepsTab';
import { useFormikContext } from 'formik';
import { IFormikContextType } from 'hooks/useFormikField';
import { useStoreState } from 'providers/StoreStateContext';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { PiPaperPlaneTilt, PiUserPlus, PiWaveform } from 'react-icons/pi';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { PopupActions } from 'reactjs-popup/dist/types';
import { IArtistResponse } from 'services/artists/types';
import { ARTIST_ROLE, IArtistWithRoleForm, STATUS } from 'services/types';
import { BUTTON_VARIANT } from 'types/components/button/button.interface';
import { measuring } from 'utils/dnd/dndUtilities';

export const artistRoleValues: IArtistWithRoleForm = {
  nickname: '',
  id: '',
  role: ARTIST_ROLE.unknown,
  artistStatus: STATUS.draft,
  isDuplicated: false,
  watchListArtist: false,
  previewPicture: undefined,
};

type ParentType = 'TRACK' | 'VIDEO' | 'MUSIC_PRODUCT_DIGITAL';

interface IArtistsRoleTabProps {
  propertyName?: 'artists';
  parent: ParentType;
}

const NAVIGATION_MAP = {
  MUSIC_PRODUCT_DIGITAL: {
    storeKey: 'musicProductDigitalForm' as const,
    basePath: 'music-product-digital',
    back: 'data',
    hint: 'music-product-digital-artists',
    next: 'delivery',
    isLast: true,
    nextIcon: undefined,
  },
  TRACK: {
    storeKey: 'trackForm' as const,
    basePath: 'track',
    next: undefined,
    back: 'data',
    hint: 'track-artists',
    isLast: true,
    nextIcon: <PiPaperPlaneTilt />,
  },
  VIDEO: {
    storeKey: 'videoForm' as const,
    basePath: 'video',
    next: undefined,
    back: 'data',
    hint: 'video-artists',
    isLast: false,
    nextIcon: <PiPaperPlaneTilt />,
  },
};

const ArtistsRoleTab = <
  Form extends Record<'artists', IArtistWithRoleForm[]> & Record<'id', string>,
>({
  propertyName = 'artists',
  parent,
}: IArtistsRoleTabProps) => {
  const { values, status, setFieldValue }: IFormikContextType<Form> = useFormikContext<Form>();
  const { t } = useTranslation();
  const { setForm, musicProductDigitalForm, musicProductPhysicalForm } = useStoreState();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();

  const config = NAVIGATION_MAP[parent];

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5,
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 200,
        tolerance: 5,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const sortableItems = useMemo(
    () =>
      values[propertyName].map((artist, index) => ({
        ...artist,
        dndId: `${artist.id}-${artist.role}-${index}`,
        originalIndex: index,
      })),
    [values, propertyName],
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (active && over && active.id !== over.id) {
      const oldIndex = sortableItems.find((item) => item.dndId === active.id)?.originalIndex;
      const newIndex = sortableItems.find((item) => item.dndId === over.id)?.originalIndex;

      if (oldIndex !== undefined && newIndex !== undefined) {
        const movedArray = arrayMove(values[propertyName], oldIndex, newIndex);
        setFieldValue(propertyName, movedArray);
      }
    }
    document.body.style.cursor = '';
  };

  const dropAnimationConfig: DropAnimation = {
    sideEffects: defaultDropAnimationSideEffects({
      styles: { active: { opacity: '0.8' } },
    }),
  };

  const handleNavigate = (direction: 'next' | 'back') => {
    if (!config) return;

    setForm(config.storeKey, values as any);

    const subPath = config[direction];
    const action = values.id ? `edit/${values.id}` : 'create';

    navigate(`/${config.basePath}/${action}/${subPath}`);
  };

  const editRoleArtistId = params.get('artistId');

  const onSelectArtist = (artist: IArtistResponse, actions: PopupActions) => {
    setParams({ artistId: artist.id });
    if (!values.artists.find((a) => a.id === artist.id)) {
      setFieldValue(propertyName, [
        ...values[propertyName],
        {
          nickname: artist.nickname,
          id: artist.id,
          role: ARTIST_ROLE.unknown,
          artistStatus: artist.artistStatus,
          isDuplicated: false,
          watchListArtist: false,
          previewPicture: artist.profilePictureLink,
        },
      ]);
    }
    actions.close();
  };

  const mergeSelectedRolesToRelease = (roles: ARTIST_ROLE[], actions: PopupActions) => {
    const currentArtists = [...values[propertyName]];

    const existingArtistData = values[propertyName].find(
      (artist) => artist.id === editRoleArtistId,
    );

    if (!existingArtistData) {
      setParams({});
      actions.close();
      return;
    }

    let updatedList = currentArtists.filter((artist) => {
      if (artist.id !== editRoleArtistId) return true;
      return roles.includes(artist.role);
    });

    const existingRolesForThisArtist = updatedList
      .filter((artist) => artist.id === editRoleArtistId)
      .map((artist) => artist.role);

    const rolesToAdd = roles.filter((role) => !existingRolesForThisArtist.includes(role));

    const newArtistEntries = rolesToAdd.map((role) => ({
      ...existingArtistData,
      role: role,
    }));

    const finalResult = [...updatedList, ...newArtistEntries];

    const finalFiltered = finalResult.filter((a) => a.role !== ARTIST_ROLE.unknown);

    setFieldValue(propertyName, finalFiltered);

    actions.close();
    setParams({});
  };

  const lastName = useMemo(() => {
    if (
      (parent === 'TRACK' || parent === 'VIDEO') &&
      (musicProductDigitalForm || musicProductPhysicalForm)
    ) {
      return 'save-and-back';
    }

    if (parent === 'MUSIC_PRODUCT_DIGITAL') {
      return 'delivery';
    }

    return 'send-to-verification';
  }, [musicProductDigitalForm, musicProductPhysicalForm, parent]);

  return (
    <StepsTab
      nextName={config.isLast ? lastName : config?.next}
      backName={config?.back}
      onNext={() => !config.isLast && handleNavigate('next')}
      onBack={() => handleNavigate('back')}
      isLast={config.isLast}
      nextIcon={config.isLast ? config?.nextIcon : undefined}
    >
      <ViewHint hintId={config?.hint} />

      {!status?.displayMode && (
        <div className="flex flex-col gap-4 w-full sm:flex-row">
          {parent === 'MUSIC_PRODUCT_DIGITAL' && (
            <Button fullWidth variant={BUTTON_VARIANT.white} size="large" disabled>
              <Button.Icon icon={<PiWaveform />} />
              <Button.Text text={t('button.import-artists-from-track')} />
            </Button>
          )}

          <ArtistSelectPopupForm
            onSelectArtist={onSelectArtist}
            onCreateNewArtist={() => {
              setForm(config.storeKey, values as any);
              navigate(`/artist/create/data`);
            }}
            trigger={
              <Button fullWidth variant={BUTTON_VARIANT.white} size="large">
                <Button.Icon icon={<PiUserPlus />} />
                <Button.Text text={t('button.add-artist-to-release')} />
              </Button>
            }
          />
        </div>
      )}

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        measuring={measuring}
        onDragStart={() => (document.body.style.cursor = 'grabbing')}
        onDragEnd={handleDragEnd}
        onDragCancel={() => (document.body.style.cursor = '')}
      >
        <SortableContext
          items={sortableItems.map((i) => i.dndId)}
          strategy={verticalListSortingStrategy}
        >
          {sortableItems.length ? (
            sortableItems.map((item, index) => (
              <ArtistRoleCard
                key={item.dndId}
                index={index}
                artist={values[propertyName][index]}
                sortableKey={item.dndId}
                name={propertyName}
                displayMode={status?.displayMode}
                onRemove={() =>
                  setFieldValue(
                    propertyName,
                    values[propertyName].filter((_, i) => i !== index),
                  )
                }
                onEdit={() => setParams({ artistId: item.id })}
              />
            ))
          ) : (
            <EmptyPlaceholder />
          )}
        </SortableContext>
      </DndContext>
      {!!editRoleArtistId && (
        <ArtistRoleSelectPopupForm
          open
          onSelectArtistRoles={mergeSelectedRolesToRelease}
          initialRoles={values.artists
            .filter((artist) => artist.id === editRoleArtistId)
            .map((artist) => artist.role)}
        />
      )}
    </StepsTab>
  );
};

export default ArtistsRoleTab;
